import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Brain, Heartbeat, Heart, BookOpen, Target, CheckCircle, Fingerprint } from '@phosphor-icons/react';
import axios from 'axios';
import PageShell from './PageShell';
import VoiceTextarea from './VoiceTextarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BLOCK_META = {
  wesen: { icon: Heart, label: 'Wesen', color: '#5BB8C4' },
  geist: { icon: Brain, label: 'Geist', color: '#8B5CF6' },
  koerper: { icon: Heartbeat, label: 'Körper', color: '#EF4444' },
  biografie: { icon: BookOpen, label: 'Biografie', color: '#F59E0B' },
};

const PhasenScan = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('intro'); // intro | wesen | geist | koerper | biografie | task | done
  const [answers, setAnswers] = useState({}); // keyed by question.id
  const [taskCompleted, setTaskCompleted] = useState(false);
  const [taskReflection, setTaskReflection] = useState('');
  const [reflectionError, setReflectionError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/phase-content/${sessionId}`).then(r => {
      setData(r.data);
      setAnswers(r.data.answers || {});
      setTaskCompleted(r.data.task_completed || false);
      setTaskReflection(r.data.task_reflection || '');
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, [sessionId]);

  const order = ['wesen', 'geist', 'koerper', 'biografie'];
  const currentIdx = order.indexOf(phase);
  const totalSteps = order.length + 2; // intro + 4 + task
  const stepNum = phase === 'intro' ? 0 : phase === 'task' ? 5 : currentIdx + 1;

  const setAnswer = (key, val) => setAnswers(prev => ({ ...prev, [key]: val }));

  // Counts sentence-ending punctuation (. ! ?) — proxies for "3 sentences"
  const reflectionSentences = (taskReflection.match(/[.!?]+/g) || []).length;
  const reflectionTrimmed = taskReflection.trim();
  const reflectionValid = reflectionSentences >= 3 && reflectionTrimmed.length >= 30;

  const persist = async (extra = {}) => {
    try {
      await axios.post(`${API}/user/phase-answers`, {
        session_id: sessionId,
        answers,
        task_completed: taskCompleted,
        task_reflection: taskReflection,
        ...extra
      });
    } catch (e) { console.error(e); }
  };

  const goNext = async () => {
    // Skip persist when user is in task step but reflection invalid (avoid 400)
    if (phase !== 'task') await persist();
    const sequence = ['intro', ...order, 'task', 'done'];
    const idx = sequence.indexOf(phase);
    setPhase(sequence[idx + 1]);
    window.scrollTo(0, 0);
  };

  const goBack = () => {
    const sequence = ['intro', ...order, 'task', 'done'];
    const idx = sequence.indexOf(phase);
    if (idx > 0) setPhase(sequence[idx - 1]);
    else navigate(`/dashboard/${sessionId}`);
  };

  const handleFinish = async () => {
    setReflectionError('');
    if (taskCompleted && !reflectionValid) {
      setReflectionError('Bitte teile drei ehrliche Sätze deiner Reflexion.');
      return;
    }
    setSubmitting(true);
    try {
      await persist({ task_completed: taskCompleted, task_reflection: taskReflection });
      navigate(`/dashboard/${sessionId}`);
    } catch (e) {
      setReflectionError('Speichern hat nicht geklappt. Versuche es bitte erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  // At least one of three questions per block must be filled to proceed
  const blockHasAnswer = (blockKey) => {
    if (!data) return false;
    const qs = data.content.questions[blockKey] || [];
    return qs.some(q => (answers[q.id] || '').trim().length > 0);
  };

  if (loading) {
    return (
      <PageShell>
        <div className="py-20 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>Lade Phasen-Scan…</div>
      </PageShell>
    );
  }
  if (!data) {
    return (
      <PageShell>
        <div className="py-20 text-center">
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Bitte zuerst Feld 1 versiegeln.</p>
          <button onClick={() => navigate(`/dashboard/${sessionId}`)} className="page-shell-btn mt-4">
            Zurück zum Dashboard
          </button>
        </div>
      </PageShell>
    );
  }

  const { stage, content } = data;

  return (
    <PageShell
      title="Phasen-Scan"
      subtitle={`${stage.name} · ${stage.focus}`}
      icon={Fingerprint}
      onClose={() => navigate(`/dashboard/${sessionId}`)}
      badge={`Schritt ${stepNum} / ${totalSteps - 1}`}
      maxWidth="max-w-2xl"
      testId="phasen-scan-page"
    >
      {/* Progress bar */}
      <div className="mb-5 -mt-1">
        <div className="h-1" style={{ background: 'var(--border-default)', borderRadius: 9999, overflow: 'hidden' }}>
          <motion.div animate={{ width: `${(stepNum / (totalSteps - 1)) * 100}%` }} transition={{ duration: 0.4 }}
            className="h-full" style={{ background: 'var(--accent-cyan)', borderRadius: 9999 }} />
        </div>
      </div>
      <AnimatePresence mode="wait">
        {phase === 'intro' && (
          <motion.div key="intro" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
            <div className="p-5 mb-5" style={{
              background: 'linear-gradient(135deg, rgba(91,184,196,0.12) 0%, rgba(91,184,196,0.02) 100%)',
              border: '1px solid rgba(91,184,196,0.3)', borderRadius: '18px',
            }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>
                DEEPDIVE-STATUS
              </p>
              <p className="text-3xl font-black mb-1" style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                {stage.name}
              </p>
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{content.focus_theme}</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Dieser Scan ist auf deine Lebensphase zugeschnitten: pro Block (Wesen, Geist, Körper, Biografie) findest du drei Fragen — wähle die, die dich am ehrlichsten anspricht, oder beantworte mehrere. Am Ende wartet eine echte strategische Aufgabe. Was du hier teilst, prägt deine Strategische Projektion.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5">
              {order.map(b => {
                const meta = BLOCK_META[b];
                const Icon = meta.icon;
                const qs = content.questions[b] || [];
                const themePreview = qs.map(q => q.hint?.split('·')[0]?.trim()).filter(Boolean).slice(0, 3).join(' · ');
                return (
                  <div key={b} className="p-4" style={{
                    background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '14px',
                  }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={16} weight="duotone" style={{ color: meta.color }} />
                      <p className="text-xs font-semibold" style={{ color: meta.color, letterSpacing: '0.08em' }}>
                        {meta.label.toUpperCase()}
                      </p>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      {themePreview || `${qs.length} Fragen`}
                    </p>
                  </div>
                );
              })}
            </div>
            <button onClick={goNext} className="page-shell-btn w-full" data-testid="phase-scan-start">
              Phasen-Scan starten <ArrowRight size={16} weight="bold" />
            </button>
          </motion.div>
        )}

        {order.includes(phase) && (() => {
          const qs = content.questions[phase] || [];
          const meta = BLOCK_META[phase];
          const Icon = meta.icon;
          const canProceed = blockHasAnswer(phase);
          return (
            <motion.div key={phase} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}>
              <div className="p-5 mb-5" style={{
                background: `${meta.color}0A`, border: `1px solid ${meta.color}30`, borderRadius: '18px',
              }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 12, background: meta.color }}>
                    <Icon size={20} weight="duotone" color="#fff" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold" style={{ color: meta.color, letterSpacing: '0.1em' }}>
                      BLOCK {meta.label.toUpperCase()}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Drei Perspektiven — beantworte mindestens eine. Mehr ist erlaubt, nicht Pflicht.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {qs.map((q, idx) => {
                    const val = answers[q.id] || '';
                    return (
                      <div key={q.id} className="p-4" style={{
                        background: 'var(--bg-surface)',
                        border: `1px solid ${val.trim() ? meta.color + '60' : 'var(--border-default)'}`,
                        borderRadius: '14px',
                        transition: 'border-color 0.2s ease',
                      }}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)', lineHeight: 1.45 }}>
                            <span style={{ color: meta.color, marginRight: 6 }}>{idx + 1}.</span>
                            {q.prompt}
                          </p>
                        </div>
                        <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                          {q.hint}
                        </p>
                        <VoiceTextarea
                          value={val}
                          onChange={(e) => setAnswer(q.id, e.target.value)}
                          placeholder="In deinen eigenen Worten — keine Korrektur, keine Performance."
                          rows={4}
                          testId={`phase-textarea-${phase}-${idx + 1}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <button onClick={goBack} className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold"
                  style={{ background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '10px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                  data-testid={`phase-back-${phase}`}>
                  <ArrowLeft size={14} /> Zurück
                </button>
                <button onClick={goNext} disabled={!canProceed}
                  className="flex items-center gap-2 px-6 py-2.5 text-xs font-semibold"
                  style={{
                    background: canProceed ? 'var(--accent-cyan)' : 'var(--border-default)',
                    color: '#fff', border: 'none', borderRadius: '10px',
                    cursor: canProceed ? 'pointer' : 'not-allowed',
                    boxShadow: canProceed ? '0 4px 14px rgba(91,184,196,0.3)' : 'none',
                    opacity: canProceed ? 1 : 0.5,
                  }}
                  data-testid={`phase-next-${phase}`}>
                  Weiter <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            </motion.div>
          );
        })()}

        {phase === 'task' && (
          <motion.div key="task" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
            <div className="p-5 mb-5" style={{
              background: 'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(245,158,11,0.02) 100%)',
              border: '1px solid rgba(245,158,11,0.35)', borderRadius: '18px',
            }}>
              <div className="flex items-center gap-2 mb-2">
                <Target size={20} weight="duotone" style={{ color: '#F59E0B' }} />
                <p className="text-xs font-semibold" style={{ color: '#F59E0B', letterSpacing: '0.1em' }}>
                  STRATEGISCHE AUFGABE · {stage.name.toUpperCase()}
                </p>
              </div>
              <p className="text-xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>
                {content.strategic_task.title}
              </p>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                {content.strategic_task.description}
              </p>
              <div className="text-xs px-3 py-1.5 inline-block font-semibold"
                style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', borderRadius: 9999 }}>
                Aufwand: ca. {content.strategic_task.duration_h}h
              </div>
            </div>
            <button onClick={() => { setTaskCompleted(!taskCompleted); setReflectionError(''); }}
              className="w-full p-4 mb-4 text-left transition-all"
              style={{
                background: taskCompleted ? 'rgba(34,197,94,0.1)' : 'var(--bg-surface)',
                border: `1px solid ${taskCompleted ? 'rgba(34,197,94,0.5)' : 'var(--border-default)'}`,
                borderRadius: '14px', cursor: 'pointer',
              }}
              data-testid="phase-task-toggle">
              <div className="flex items-center gap-3">
                <CheckCircle size={22} weight={taskCompleted ? 'fill' : 'regular'}
                  style={{ color: taskCompleted ? '#16A34A' : 'var(--text-secondary)' }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {taskCompleted ? 'Aufgabe als abgeschlossen markiert' : 'Hast du die Aufgabe ehrlich erledigt?'}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    Klick, um den Status zu wechseln. Wir vertrauen dir — die Tiefe deines Dossiers wächst mit deiner Ehrlichkeit.
                  </p>
                </div>
              </div>
            </button>

            {/* Self-Reflection (only when task is marked done) */}
            <AnimatePresence>
              {taskCompleted && (
                <motion.div
                  key="reflection"
                  initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  style={{ overflow: 'hidden' }}>
                  <div className="p-4" style={{
                    background: 'var(--bg-surface)',
                    border: `1.5px solid ${reflectionValid ? 'rgba(34,197,94,0.5)' : reflectionError ? 'rgba(220,38,38,0.45)' : 'var(--border-default)'}`,
                    borderRadius: '14px',
                    transition: 'border-color 0.25s ease',
                  }}>
                    <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                      Deine ehrliche Reflexion · 3 Sätze
                    </p>
                    <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      Was hat sich beim Erledigen verändert? Was ist klarer geworden? Was nimmst du mit?<br/>
                      Drei kurze Sätze reichen — ohne Druck, ohne Korrektur. Diese Worte machen dein Dossier zu deinem.
                    </p>
                    <VoiceTextarea
                      value={taskReflection}
                      onChange={(e) => { setTaskReflection(e.target.value); if (reflectionError) setReflectionError(''); }}
                      placeholder="In deinen Worten — drei Sätze, die du dir selbst aufschreibst."
                      rows={5}
                      testId="phase-task-reflection-textarea"
                    />
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span style={{
                        color: reflectionValid ? '#16A34A' : 'var(--text-secondary)',
                        fontWeight: reflectionValid ? 600 : 500,
                      }} data-testid="phase-task-reflection-counter">
                        {reflectionValid
                          ? '✓ Drei Sätze sind notiert.'
                          : `${reflectionSentences} / 3 Sätze · ${reflectionTrimmed.length} Zeichen`}
                      </span>
                      {reflectionError && (
                        <span style={{ color: '#DC2626' }} data-testid="phase-task-reflection-error">
                          {reflectionError}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="flex items-center justify-between gap-3">
              <button onClick={goBack} className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold"
                style={{ background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '10px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                data-testid="phase-back-task">
                <ArrowLeft size={14} /> Zurück
              </button>
              <button onClick={handleFinish} disabled={submitting || (taskCompleted && !reflectionValid)}
                className="flex items-center gap-2 px-6 py-2.5 text-xs font-semibold"
                style={{
                  background: (taskCompleted && !reflectionValid) ? 'var(--border-default)' : 'var(--accent-cyan)',
                  color: '#fff', border: 'none', borderRadius: '10px',
                  cursor: submitting ? 'wait' : (taskCompleted && !reflectionValid ? 'not-allowed' : 'pointer'),
                  boxShadow: (taskCompleted && !reflectionValid) ? 'none' : '0 4px 14px rgba(91,184,196,0.3)',
                  opacity: (taskCompleted && !reflectionValid) ? 0.55 : 1,
                }}
                data-testid="phase-scan-finish">
                {submitting ? 'Speichert…' : 'Phasen-Scan abschließen'} <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageShell>
  );
};

export default PhasenScan;
